import { FileText, LineChart, Search, Sunrise, Table2, Users } from "lucide-react";
import type React from "react";

import { m } from "@/paraglide/messages.js";

interface ResearchLibraryProps {
  className?: string;
}

/**
 * Bento illustration: the research library. A searchable list of the archives
 * a firm already owns, each marked as indexed once it has been converted into
 * queryable data. Icons are lucide glyphs so there is no asset dependency.
 */
const ResearchLibraryIllustration: React.FC<ResearchLibraryProps> = ({ className = "" }) => {
  const sources = [
    { name: m.landing_library_equity_research(), icon: FileText, indexed: true },
    { name: m.landing_library_ficc_notes(), icon: LineChart, indexed: true },
    { name: m.landing_library_morning_briefings(), icon: Sunrise, indexed: true },
    { name: m.landing_library_analyst_models(), icon: Table2 },
    { name: m.landing_library_coverage_map(), icon: Users },
  ];

  return (
    <div
      className={`flex h-full w-full items-center justify-center p-4 ${className}`}
      role="img"
      aria-label={m.landing_capability_alpha_illustration_alt()}
    >
      <div className="w-[345px] -mb-2 overflow-hidden rounded-t-[9.628px] border-[0.802px] border-b-0 border-border bg-gradient-to-b from-background to-transparent backdrop-blur-[16px]">
        {/* Search header */}
        <div className="flex items-center gap-[12.837px] border-b-[0.802px] border-border px-[12.837px] py-[8.826px]">
          <Search className="size-[14.442px] shrink-0 text-muted-foreground" />
          <span className="whitespace-nowrap font-sans text-[12.837px] font-normal leading-[19.256px] text-muted-foreground">
            {m.landing_capability_alpha_search()}
          </span>
        </div>

        {/* Source list */}
        {sources.map((source, index) => {
          const Icon = source.icon;
          return (
            <div
              key={source.name}
              className={`flex items-center justify-between px-[12.837px] py-[8.826px] ${
                index < sources.length - 1 ? "border-b-[0.479px] border-border" : ""
              }`}
            >
              <div className="flex items-center gap-[12.837px]">
                <div className="flex size-6 shrink-0 items-center justify-center">
                  <Icon className="size-[18px] text-foreground" />
                </div>
                <span className="whitespace-nowrap font-sans text-[12.837px] font-normal leading-[19.256px] text-muted-foreground">
                  {source.name}
                </span>
              </div>
              {source.indexed && (
                <div className="flex items-center justify-center rounded-[3.295px] bg-[color-mix(in_oklch,oklch(0.72_0.19_145)_18%,transparent)] px-[5.272px] py-[1.318px]">
                  <span className="whitespace-nowrap font-sans text-[9.583px] font-medium leading-[15.333px] text-[oklch(0.78_0.18_145)]">
                    {m.landing_capability_alpha_indexed()}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { ResearchLibraryIllustration as ResearchLibrary };
